import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  describeFailure,
  describeTotals,
  importInBatches,
  parseImportCards,
  postImportBatch,
} from "../import-batches";

/**
 * Bulk import from a Postcrossing profile.
 *
 * This was a server component that returned "Not authorized" before rendering. As a client
 * page the ownership check is cosmetic — the real gate is server-side on
 * /api/import/postcrossing, which returns 401/403 regardless of what the UI shows.
 */
export default function PostcrossingImportPage() {
  const [isOwner, setIsOwner] = useState<boolean | null>(null);
  const [cardsJson, setCardsJson] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch("/api/me", { cache: "no-store" })
      .then((response) => response.json())
      .then((viewer: { isOwner: boolean }) => setIsOwner(viewer.isOwner))
      .catch(() => setIsOwner(false));
  }, []);

  async function importCards(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setStatus("Importing cards…");
    try {
      const cards = parseImportCards(cardsJson);
      if (cards.length === 0) throw new Error("Paste at least one card to import.");
      // A few hundred cards in one request can outlast the server's write timeout, so
      // they go up in small batches — see import-batches.ts.
      const totals = await importInBatches(cards, postImportBatch, {
        onProgress: (saved, remaining) =>
          setStatus(
            `Importing cards… ${cards.length - remaining} of ${cards.length} done ` +
              `(${saved.importedCount} saved, ${saved.skippedCount} already there).`,
          ),
      });
      setStatus(`${describeTotals(totals)}.`);
      setCardsJson("");
    } catch (error) {
      setStatus(describeFailure(error));
    } finally {
      setBusy(false);
    }
  }

  if (isOwner === null) return <main className="app-shell"><p>Loading…</p></main>;
  if (!isOwner) {
    return (
      <main className="app-shell">
        <section className="empty-state">
          <div className="empty-stamp">✉</div>
          <h1>Not authorized</h1>
          <p>
            <Link to="/signin">Sign in as the collection owner</Link> to import postcards.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <section className="empty-state">
        <div className="empty-stamp">✉</div>
        <h1>Postcrossing import</h1>
        <form onSubmit={importCards}>
          <label htmlFor="cardsJson">Cards JSON</label>
          <textarea
            id="cardsJson"
            name="cardsJson"
            value={cardsJson}
            onChange={(event) => setCardsJson(event.target.value)}
            required
            rows={12}
          />
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? "Importing…" : "Import Postcrossing cards"}
          </button>
        </form>
        <p role="status" aria-live="polite">{status}</p>
        <p>
          <Link to="/">← Back to the archive</Link>
        </p>
      </section>
    </main>
  );
}
